import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../css/privacy.css';

/* ── Edit these two values ─────────────────────────────────────────── */
const CONTACT_EMAIL = 'theeliteglobalreview@gmail.com';                 // e.g. 'support@theeliteglobalreview.com'
const LAST_UPDATED  = 'October 14, 2026'; // update whenever the policy changes
/* ──────────────────────────────────────────────────────────────────── */

const WEBSITE_URL = 'https://theeliteglobalreview.com';


const INTRO =
  'At The Elite Global Review, we respect your privacy and are committed to protecting the personal information you share with us. Whether you are a reader, subscriber, business leader, or contributor, we want you to feel confident when connecting with our magazine.';

/* ── Icons (inline SVG, inherit colour from CSS) ──────────────────── */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ICONS = {
  collect: (
    <Icon>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.5 20c.9-3.6 3.9-5.6 7.5-5.6s6.6 2 7.5 5.6" />
    </Icon>
  ),
  use: (
    <Icon>
      <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
      <circle cx="15" cy="7" r="2" />
      <circle cx="9" cy="17" r="2" />
    </Icon>
  ),
  cookies: (
    <Icon>
      <path d="M21 12.2A9 9 0 1 1 11.8 3a4.2 4.2 0 0 0 5 5 3.6 3.6 0 0 0 4.2 4.2Z" />
      <circle cx="8.5" cy="11" r=".9" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="15.5" r=".9" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="11.5" r=".9" fill="currentColor" stroke="none" />
    </Icon>
  ),
  security: (
    <Icon>
      <path d="M12 3 4.5 6v5.6c0 4.4 3.1 7.9 7.5 9.4 4.4-1.5 7.5-5 7.5-9.4V6L12 3Z" />
      <path d="m9 12 2.2 2.2L15.2 10" />
    </Icon>
  ),
  updates: (
    <Icon>
      <path d="M20 11a8 8 0 0 0-14.3-4.6L4 8.5" />
      <path d="M4 4v4.5h4.5" />
      <path d="M4 13a8 8 0 0 0 14.3 4.6l1.7-2.1" />
      <path d="M20 20v-4.5h-4.5" />
    </Icon>
  ),
  contact: (
    <Icon>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m4 7.5 8 6 8-6" />
    </Icon>
  ),
};

/* `note: true` renders a paragraph as a highlighted callout */
const SECTIONS = [
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    icon: 'collect',
    body: [
      { text: 'We may collect information such as your name, email address, contact details, and other relevant information when you subscribe to our updates, submit articles or interview responses, participate in magazine features, or interact with our website.' },
    ],
  },
  {
    id: 'how-we-use-your-information',
    title: 'How We Use Your Information',
    icon: 'use',
    body: [
      { text: 'We use the information we collect to improve your experience, enhance our services, respond to your inquiries, share relevant updates, and improve our website and editorial offerings.' },
      { text: 'We do not sell your personal information. We may share it with service providers when necessary to operate our website or provide our services, or when disclosure is required by law. We handle personal information in accordance with applicable privacy requirements.', note: true },
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies and Website Experience',
    icon: 'cookies',
    body: [
      { text: 'Our website may use cookies and similar technologies to understand visitor activity, analyze website traffic, improve performance, and make browsing more convenient. You can manage cookies through your browser settings. Where required, we will obtain your consent before using non-essential cookies.' },
    ],
  },
  {
    id: 'protecting-your-information',
    title: 'Protecting Your Information',
    icon: 'security',
    body: [
      { text: 'We take reasonable security measures to protect your personal information against unauthorized access, misuse, loss, or disclosure. However, no online transmission or storage system can be guaranteed to be completely secure.' },
    ],
  },
  {
    id: 'updates',
    title: 'Updates to This Policy',
    icon: 'updates',
    body: [
      { text: 'We may update this Privacy Policy as our website and services evolve. Any changes will be published on this page, and we encourage you to review it periodically.' },
    ],
  },
];

const CONTACT_ID = 'contact-us';
const TOC = [...SECTIONS.map(({ id, title }) => ({ id, title })), { id: CONTACT_ID, title: 'Contact Us' }];

/* Short summary cards under the hero — each one restates the policy below */
const HIGHLIGHTS = [
  { icon: 'security', title: 'We do not sell your data', text: 'Your personal information is never sold.', to: 'how-we-use-your-information' },
  { icon: 'cookies',  title: 'You control cookies',      text: 'Manage them in your browser settings.',   to: 'cookies' },
  { icon: 'updates',  title: 'Clear updates',            text: 'Any changes are posted on this page.',     to: 'updates' },
];

export default function PrivacyPolicy() {
  const [active, setActive]     = useState(TOC[0].id);
  const [progress, setProgress] = useState(0);
  const [tocStyle, setTocStyle] = useState(null);
  const tocRef     = useRef(null);
  const articleRef = useRef(null);

  /* Fix the TOC (position: fixed) once it reaches the navbar offset,
     and release it at the end of the article */
  useEffect(() => {
    const NAV = 112; // keep in sync with --pp-nav-space
    const update = () => {
      const col = tocRef.current;
      const art = articleRef.current;
      if (!col || !art || window.innerWidth <= 960) { setTocStyle(null); return; }
      const c = col.getBoundingClientRect();
      const a = art.getBoundingClientRect();
      const innerH = col.firstElementChild.offsetHeight;
      if (c.top <= NAV) {
        setTocStyle({
          position: 'fixed',
          top: Math.min(NAV, a.bottom - innerH),
          left: c.left,
          width: c.width,
        });
      } else {
        setTocStyle(null);
      }
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  /* Scroll-spy: highlight the section currently in view */
  useEffect(() => {
    const els = TOC.map(t => document.getElementById(t.id)).filter(Boolean);
    if (!('IntersectionObserver' in window) || !els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActive(visible[0].target.id);
        }
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  /* Thin reading-progress bar */
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const h = document.documentElement;
        const max = h.scrollHeight - h.clientHeight;
        setProgress(max > 0 ? Math.min(100, (h.scrollTop / max) * 100) : 0);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const goTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    setActive(id);
    if (window.history?.replaceState) window.history.replaceState(null, '', `#${id}`);
  };

  return (
    <main className="pp-page">
      <div className="pp-progress" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>

      {/* ── Header ───────────────────────────────────────────── */}
      <header className="pp-hero">
        <div className="pp-wrap">
          <nav className="pp-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">Privacy Policy</span>
          </nav>

          <h1 className="pp-title">Privacy Policy</h1>
          <span className="pp-rule" aria-hidden="true" />
          <p className="pp-intro">{INTRO}</p>

          <p className="pp-updated">
            <span className="pp-updated-dot" aria-hidden="true" />
            Last updated <time>{LAST_UPDATED}</time>
          </p>
        </div>
      </header>

      {/* ── Highlights ───────────────────────────────────────── */}
      <div className="pp-wrap pp-highlights-wrap">
        <ul className="pp-highlights">
          {HIGHLIGHTS.map(h => (
            <li key={h.title}>
              <a href={`#${h.to}`} onClick={(e) => goTo(e, h.to)} className="pp-highlight">
                <span className="pp-icon">{ICONS[h.icon]}</span>
                <span className="pp-highlight-text">
                  <strong>{h.title}</strong>
                  <span>{h.text}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="pp-wrap pp-layout">
        <aside className="pp-toc" ref={tocRef} aria-label="On this page">
          <div className="pp-toc-inner" style={tocStyle || undefined}>
          <p className="pp-toc-title">On this page</p>
          <ol>
            {TOC.map((t, i) => (
              <li key={t.id}>
                <a
                  href={`#${t.id}`}
                  onClick={(e) => goTo(e, t.id)}
                  className={active === t.id ? 'is-active' : ''}
                  aria-current={active === t.id ? 'true' : undefined}
                >
                  <span className="pp-toc-num" aria-hidden="true">{i + 1}</span>
                  {t.title}
                </a>
              </li>
            ))}
          </ol>
          </div>
        </aside>

        <article className="pp-article" ref={articleRef}>
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="pp-section" aria-labelledby={`${s.id}-h`}>
              <div className="pp-section-head">
                <span className="pp-icon">{ICONS[s.icon]}</span>
                <h2 id={`${s.id}-h`}>{s.title}</h2>
              </div>
              {s.body.map((p, j) => (
                <p key={j} className={p.note ? 'pp-note' : undefined}>{p.text}</p>
              ))}
            </section>
          ))}

          <section id={CONTACT_ID} className="pp-section pp-section-contact" aria-labelledby={`${CONTACT_ID}-h`}>
            <div className="pp-section-head">
              <span className="pp-icon">{ICONS.contact}</span>
              <h2 id={`${CONTACT_ID}-h`}>Contact Us</h2>
            </div>
            <p>
              If you have questions about this Privacy Policy or how we handle your personal
              information, please contact us at our official support email address.
            </p>

            <div className="pp-contact">
              <p className="pp-contact-name">The Elite Global Review</p>
              <dl>
                <div>
                  <dt>Website</dt>
                  <dd>
                    <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">
                      theeliteglobalreview.com
                    </a>
                  </dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    {CONTACT_EMAIL
                      ? <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                      : <span className="pp-placeholder">[Insert your official contact email]</span>}
                  </dd>
                </div>
              </dl>

              {CONTACT_EMAIL && (
                <a className="pp-btn" href={`mailto:${CONTACT_EMAIL}`}>Email our team</a>
              )}
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}